import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { api, errText } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { toast } from "sonner";
import { Activity, CheckCircle2, XCircle, MinusCircle, Timer, Moon } from "lucide-react";

const SLOW_S = 5;
const HealthHistoryLazy = lazy(() => import("./HealthHistory").then((m) => ({ default: m.HealthHistory })));
const HealthHistory = (props) => <Suspense fallback={null}><HealthHistoryLazy {...props} /></Suspense>;

const OPEN_TO = ["Everyone", "Signed-in members"];
const PARALLEL = 3;
const TIMEOUT = 12000;
const BAD_TEXT = /Compiled with problems|Something went wrong|The app hit an error|Uncaught runtime errors/i;

const resolvePath = (path, s) => {
  if (path.startsWith("/seller/")) return s.seller ? `/seller/${s.seller}` : null;
  const out = path.replace(/:id|:listingId/g, (m) => s[m] || "");
  return out.includes(":") ? null : out;
};

const checkPage = (url) => new Promise((resolve) => {
  const start = performance.now();
  const f = document.createElement("iframe");
  f.name = "cp-health-check";
  f.setAttribute("aria-hidden", "true");
  f.style.cssText = "position:fixed;left:-20000px;top:0;width:1280px;height:800px;opacity:0;pointer-events:none";
  const errors = [];
  let done = false;
  const finish = (ok, note) => { if (done) return; done = true; clearInterval(poll); clearTimeout(timer); f.remove(); resolve({ ok, note, ms: Math.round(performance.now() - start) }); };
  const inspect = () => {
    try {
      const w = f.contentWindow;
      const doc = f.contentDocument;
      if (!w || !doc || doc.readyState !== "complete") return;
      if (!w.__cpHealthHooked) { w.__cpHealthHooked = true; w.addEventListener("error", (e) => errors.push(e.message || "Script error")); }
      const text = doc.body?.innerText || "";
      if (BAD_TEXT.test(text) || errors.length) return finish(false, errors[0] || "The page showed an error screen");
      const root = doc.getElementById("root");
      const loaded = root && root.children.length > 0 && text.trim().length > 80;
      if (!loaded) return;
      const landed = w.location.pathname;
      const want = new URL(url, window.location.origin).pathname;
      if (landed !== want) return finish(false, `Sent somewhere else (${landed})`);
      setTimeout(() => finish(errors.length === 0, errors[0] || ""), 600);
    } catch (e) { finish(false, e.message); }
  };
  const poll = setInterval(inspect, 250);
  const timer = setTimeout(() => finish(false, "Didn't finish loading within 12 seconds"), TIMEOUT);
  f.src = url;
  document.body.appendChild(f);
});

const Row = ({ r }) => {
  const Icon = r.status === "ok" ? CheckCircle2 : r.status === "fail" ? XCircle : r.status === "slow" ? Timer : MinusCircle;
  const tone = { ok: "text-emerald-600", fail: "text-red-600", slow: "text-amber-600" }[r.status] || "text-slate-400";
  const ms = r.ms ?? (r.seconds ? r.seconds * 1000 : 0);
  return (
    <tr className={`border-b border-slate-50 ${r.status === "slow" ? "bg-amber-50/60" : ""}`} data-testid={`health-row-${r.path}`} data-status={r.status}>
      <td className="py-1.5 pl-1 pr-3"><Icon size={16} className={tone} /></td>
      <td className="py-1.5 pr-4 font-mono text-[13px] text-[#12234A]">{r.url || r.path}</td>
      <td className={`py-1.5 pr-4 text-xs tabular-nums ${r.status === "slow" ? "font-bold text-amber-700" : "text-slate-400"}`}>{ms ? `${(ms / 1000).toFixed(1)}s` : ""}</td>
      <td className={`py-1.5 text-[13px] ${r.status === "fail" ? "text-red-700" : r.status === "slow" ? "text-amber-800" : "text-slate-500"}`}>
        {r.note || { ok: "Loaded fine", slow: `Slow — took over ${SLOW_S} seconds`, pending: "Waiting…" }[r.status] || ""}
      </td>
    </tr>
  );
};

const ORDER = { fail: 0, slow: 1, pending: 2, ok: 3, skip: 4 };
export const HealthTable = ({ rows }) => (
  <table className="w-full text-sm"><tbody>{[...rows].sort((a, b) => ORDER[a.status] - ORDER[b.status]).map((r) => <Row key={r.path} r={r} />)}</tbody></table>
);
const summaryText = (fails, slow) => [fails && `${fails} page${fails > 1 ? "s" : ""} need attention`, slow && `${slow} slow page${slow > 1 ? "s" : ""}`].filter(Boolean).join(" · ") || "All pages loaded fine";

const NightlyCheck = () => {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/admin/site-map/health-last").then((r) => setLast(r.data)).catch(() => setLast({})); }, []);
  const runNow = () => {
    setBusy(true);
    api.post("/admin/site-map/health-run", null, { timeout: 120000 }).then((r) => { setLast(r.data); setOpen(true); toast.success("Server check finished"); })
      .catch((e) => toast.error(errText(e))).finally(() => setBusy(false));
  };
  const ran = last?.ran_at;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 space-y-3" data-testid="health-nightly">
      <div className="flex flex-wrap items-center gap-3">
        <Moon size={18} className="text-[#12234A]" />
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold text-[#12234A]">Nightly check <span className="font-normal text-slate-500">· runs every night around 11:30pm Toronto and emails admins only if a page breaks</span></p>
          <p className={`text-sm ${last?.fails ? "text-red-700 font-semibold" : last?.slow ? "text-amber-700" : "text-slate-500"}`} data-testid="health-nightly-summary">
            {ran ? `Last run ${new Date(ran).toLocaleString()} · ${summaryText(last.fails, last.slow)}${last.emailed ? " · admins were emailed" : ""}` : "Hasn't run yet"}
          </p>
        </div>
        {ran && <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid="health-nightly-toggle">{open ? "Hide results" : "Show results"}</Button>}
        <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="health-nightly-run">{busy ? "Checking…" : "Run server check now"}</Button>
      </div>
      {open && last?.results && <HealthTable rows={last.results} />}
      <HealthHistory refreshKey={last?.ran_at} />
      <p className="text-[11px] text-slate-400">The nightly check confirms each page and the data it needs respond, and times them. The browser check below also catches on-screen errors.</p>
    </div>
  );
};

export const HealthCheck = ({ pages }) => {
  const [rows, setRows] = useState(null);
  const [running, setRunning] = useState(false);
  const cancel = useRef(false);
  const run = async () => {
    let samples;
    try { samples = (await api.get("/admin/site-map/samples")).data; } catch (e) { toast.error(errText(e)); return; }
    const list = pages.filter((p) => OPEN_TO.includes(p.access) && !/^\/(staff|admin|moderator)(\/|$)/.test(p.path)).map((p) => {
      const url = resolvePath(p.path, samples);
      return { path: p.path, url, status: url ? "pending" : "skip", note: url ? "" : "Needs a private link (e.g. from an email), so it was skipped" };
    });
    setRows(list);
    setRunning(true);
    cancel.current = false;
    const queue = list.filter((r) => r.status === "pending");
    const worker = async () => {
      while (queue.length && !cancel.current) {
        const r = queue.shift();
        const res = await checkPage(r.url);
        setRows((cur) => cur.map((x) => (x.path === r.path ? { ...x, status: !res.ok ? "fail" : res.ms > SLOW_S * 1000 ? "slow" : "ok", note: res.note, ms: res.ms } : x)));
      }
    };
    await Promise.all(Array.from({ length: PARALLEL }, worker));
    setRunning(false);
  };
  const done = rows?.filter((r) => r.status !== "pending").length || 0;
  const fails = rows?.filter((r) => r.status === "fail").length || 0;
  const slow = rows?.filter((r) => r.status === "slow").length || 0;
  return (
    <div className="space-y-4" data-testid="health-check">
      <NightlyCheck />
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 pt-2">Full browser check</p>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={run} disabled={running} className="bg-[#F5551E] hover:bg-[#e04a15] gap-1.5" data-testid="health-run"><Activity size={15} /> {running ? "Checking…" : rows ? "Run again" : "Run health check"}</Button>
        {running && <Button variant="outline" onClick={() => { cancel.current = true; }} data-testid="health-stop">Stop</Button>}
        <p className="text-sm text-slate-500">Opens every public and member page in the background and flags any that crash, hang or redirect, and highlights any slower than 5 seconds. Your visits aren't counted as ad views.</p>
      </div>
      {rows && (
        <>
          <Progress value={(done / rows.length) * 100} className="h-2" />
          <p className={`text-sm font-semibold ${fails ? "text-red-700" : slow ? "text-amber-700" : "text-emerald-700"}`} data-testid="health-summary">
            {running ? `Checked ${done} of ${rows.length}…` : summaryText(fails, slow)}
          </p>
          <HealthTable rows={rows} />
        </>
      )}
    </div>
  );
};
